import { Injectable } from '@angular/core';
import { BuildingModel } from 'src/app/core/models/building.model';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BaseService } from 'src/app/core/base/base.service';
import { ObjectApiInterface } from '../../../../core/base/interfaces/object-api.interface';
import { StageModel } from '../../../../core/models/stage.model';
import { map } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { EstablishmentModel } from '../../../../core/models/establishment.model';
import { ActivityModel } from '../../../../core/models/activity.model';
import { BuildingActivityModel } from '../../../../core/models/building-activity.model';
import { BuildingRoomModel, FloorModel } from '../../../../core/models/building-room.model';
import { AddressModel } from '../../../../core/models/address.model';
import { ContractModel } from '../../../../core/models/contract.model';
import { EquipmentModel } from '../../../../core/models/equipment.model';
import { EquipementMetrikStatusModel } from '../../../../core/models/equipement-metrik-status.model';
import { BuildingMetrikStatusModel } from '../../../../core/models/building-metrik-status.model';
import { EnergyIdModel } from '../../../../core/models/energy-id.model';
import { AddressService } from '../../../../core/services/address.service';
import { AuthService } from '../../../../core/services/auth.service';

@Injectable({
  providedIn: 'root',
})
export class BuildingService extends BaseService<BuildingModel> {
  defaultSort = 'technicalIdentifier';

  protected _rooms = new BehaviorSubject<BuildingRoomModel[]>([] as BuildingRoomModel[]);
  readonly rooms$ = this._rooms.asObservable();
  protected _address = new BehaviorSubject<AddressModel[]>([] as AddressModel[]);
  readonly address$ = this._address.asObservable();

  constructor(
    public http: HttpClient,
    public addressService: AddressService,
    public authService: AuthService,
  ) {
    super(http, 'building', authService);
  }

  checkDependencies(model: BuildingModel) {
    return this.http.post<ObjectApiInterface<any>>(`${this.baseUrlApi}/checkDependencies/${model._id}`, {});
  }

  findById(id: string): Observable<ObjectApiInterface<BuildingModel>> {
    return this.http.get<ObjectApiInterface<BuildingModel>>(`${this.baseUrlApi}/get/${id}`).pipe(
      map(x => {
        x.data.establishments = x.data?.establishments
          .filter(x => x._id)
          .map(establishment => {
            establishment.nbOfPermanentBed = establishment.activities?.reduce((p, c) => p + c.nbOfPermanantBed, 0);
            return establishment;
          });
        this._entity.next(x.data);
        return x;
      }),
    );
  }

  update(entity: BuildingModel | any): Observable<ObjectApiInterface<BuildingModel>> {
    return this.http.put<ObjectApiInterface<BuildingModel>>(`${this.baseUrlApi}/update/${entity['_id']}`, entity).pipe(
      map(x => {
        x.data.establishments = x.data?.establishments
          .filter(x => x._id)
          .map(establishment => {
            establishment.nbOfPermanentBed = establishment.activities?.reduce((p, c) => p + c.nbOfPermanantBed, 0);
            return establishment;
          });
        this._entity.next(x.data);
        return x;
      }),
    );
  }

  getRoomCategoryRelation(_id): Observable<ObjectApiInterface<BuildingModel[]>> {
    return this.http.get<ObjectApiInterface<BuildingModel[]>>(`${this.baseUrlApi}/getRoomCategoryRelation/${_id}`);
  }

  findBuildingById(id: string): Observable<ObjectApiInterface<BuildingModel>> {
    return this.http.get<ObjectApiInterface<BuildingModel>>(`${this.baseUrlApi}/get/${id}`);
  }

  /**
   * Récupère plusieurs buildings par leurs IDs en une seule requête
   * Optimisation : 1 requête au lieu de N requêtes individuelles
   */
  findByIds(ids: string[]): Observable<ObjectApiInterface<BuildingModel[]>> {
    return this.http.post<ObjectApiInterface<BuildingModel[]>>(`${this.baseUrlApi}/find-by-ids`, { ids });
  }

  findAllActivities(
    page?: number,
    perPage?: number,
    sortField: string = this.defaultSort,
    sortOrder: string = 'desc',
    search?: string,
    filters?: ActivityModel | any,
  ): Observable<ObjectApiInterface<ActivityModel[]>> {
    return this.http.get<ObjectApiInterface<ActivityModel[]>>(`${this.baseUrlApi}/activity/list`, {
      params: new HttpParams()
        .set('page', page?.toString() ?? '1')
        .set('perPage', perPage?.toString() ?? '30')
        .set('sort', `${sortField}@${sortOrder}`)
        .set('search', search ?? '')
        .set('filters', JSON.stringify(filters) ?? ''),
    });
  }

  findAllActivesBuildings(
    page?: number,
    perPage?: number,
    sortField: string = this.defaultSort,
    sortOrder: string = 'desc',
    search?: string,
    filters?: ActivityModel | any,
  ): Observable<ObjectApiInterface<ActivityModel[]>> {
    return this.http.get<ObjectApiInterface<ActivityModel[]>>(`${this.baseUrlApi}/actives`, {
      params: new HttpParams()
        .set('page', page?.toString() ?? '1')
        .set('perPage', perPage?.toString() ?? '30')
        .set('sort', `${sortField}@${sortOrder}`)
        .set('search', search ?? '')
        .set('filters', JSON.stringify(filters) ?? ''),
    });
  }

  createStage(buildingId: string, stage: any) {
    return this.http.post<ObjectApiInterface<StageModel>>(`${this.baseUrlApi}/${buildingId}/createStage`, stage).pipe(
      map(x => {
        this._entity.value.stages.push(x.data);
        this._entity.next(this._entity.value);
        return x;
      }),
    );
  }

  updateStage(buildingId: string, stage: any) {
    return this.http.put<ObjectApiInterface<StageModel>>(`${this.baseUrlApi}/${buildingId}/updateStage`, stage).pipe(
      map(x => {
        const index = this._entity.value.stages.findIndex(y => y._id === x.data._id);
        this._entity.value.stages[index] = x.data;
        this._entity.next(this._entity.value);
        return x;
      }),
    );
  }

  deleteStage(buildingId: string, stageId: any) {
    return this.http
      .delete<ObjectApiInterface<StageModel>>(`${this.baseUrlApi}/${buildingId}/deleteStage/${stageId}`)
      .pipe(
        map(x => {
          this._entity.value.stages = this._entity.value.stages.filter(y => y._id !== x.data._id);
          this._entity.next(this._entity.value);
          return x;
        }),
      );
  }

  createEnergy(buildingId: string, energy: any) {
    return this.http
      .post<ObjectApiInterface<BuildingModel>>(`${this.baseUrlApi}/${buildingId}/createEnergy`, energy)
      .pipe(
        map(x => {
          this._entity.next(x.data);
          return x;
        }),
      );
  }

  updateEnergy(buildingId: string, energy: any) {
    return this.http
      .put<ObjectApiInterface<BuildingModel>>(`${this.baseUrlApi}/${buildingId}/updateEnergy`, energy)
      .pipe(
        map(x => {
          this._entity.next(x.data);
          return x;
        }),
      );
  }

  deleteEnergy(buildingId: string, energyId: any) {
    return this.http
      .delete<ObjectApiInterface<BuildingModel>>(`${this.baseUrlApi}/${buildingId}/deleteEnergy/${energyId}`)
      .pipe(
        map(x => {
          this._entity.next(x.data);
          return x;
        }),
      );
  }

  createFloor(buildingId: string, floor: any, roomId: string) {
    return this.http
      .post<ObjectApiInterface<FloorModel>>(`${this.baseUrlApi}/${buildingId}/createFloor/${roomId}`, floor)
      .pipe(
        map(x => {
          const index = this._entity.value.rooms.findIndex(y => y._id === roomId);

          //check if rooms is an array
          if (this._entity.value.rooms[index].floor instanceof Array) {
            this._entity.value.rooms[index].floor.push(x.data);
          } else {
            this._entity.value.rooms[index].floor = [x.data];
          }
          return x;
        }),
      );
  }

  updateFloor(buildingId: string, floor: any, roomId: string) {
    return this.http
      .put<ObjectApiInterface<FloorModel>>(`${this.baseUrlApi}/${buildingId}/updateFloor/${roomId}`, floor)
      .pipe(
        map(x => {
          const index = this._entity.value.rooms.findIndex(y => y._id === roomId);
          const floorIndex = this._entity.value.rooms[index].floor.findIndex(y => y._id === x.data._id);
          this._entity.value.rooms[index].floor[floorIndex] = x.data;
          return x;
        }),
      );
  }

  deleteFloor(buildingId: string, roomId: any, floorId: string) {
    return this.http
      .delete<ObjectApiInterface<BuildingModel>>(`${this.baseUrlApi}/${buildingId}/deleteFloor/${roomId}/${floorId}`)
      .pipe(
        map(x => {
          this._entity.next(x.data);
          return x;
        }),
      );
  }

  searchRooms(search: string) {
    if (search) {
      const match = this._entity.value.rooms.filter(x => {
        const label = x.label.toLowerCase().includes(search.toLowerCase());
        const stage = x.stage?.label.toLowerCase().includes(search.toLowerCase());
        // const roomCategory = x.roomCategory?.label.toLowerCase().includes(search.toLowerCase());
        const surface = x.surface.toString().toLowerCase().includes(search.toLowerCase());
        const nbOfBed = x.nbOfBed.toString().toLowerCase().includes(search.toLowerCase());
        // return label || stage || roomCategory || surface || nbOfBed;
        return label || stage || surface || nbOfBed;
      });
      this._rooms.next(match);
    } else {
      this._rooms.next(this._entity?.value?.rooms);
    }
  }

  findAllRoom(
    buildingId: string,
    page?: number,
    perPage?: number,
    sortField: string = this.defaultSort,
    sortOrder: string = 'desc',
    search?: string,
    filters?: BuildingRoomModel | any,
  ): Observable<ObjectApiInterface<BuildingRoomModel[]>> {
    return this.http.get<ObjectApiInterface<BuildingRoomModel[]>>(`${this.baseUrlApi}/roomList/${buildingId}`, {
      params: new HttpParams()
        .set('page', page?.toString() ?? '1')
        .set('perPage', perPage?.toString() ?? '30')
        .set('sort', `${sortField}@${sortOrder}`)
        .set('search', search ?? '')
        .set('filters', JSON.stringify(filters) ?? ''),
    });
  }

  // createRoom(buildingId: string, room: any) {
  //    return this.http.post<ObjectApiInterface<BuildingRoomModel>>(`${this.baseUrlApi}/${buildingId}/createRoom`, room)
  //       .pipe(map(x => {
  //          this._entity.value.rooms.push(x.data);
  //          return x;
  //       }));
  // }
  //
  // updateRoom(buildingId: string, room: any) {
  //    return this.http.put<ObjectApiInterface<BuildingRoomModel>>(`${this.baseUrlApi}/${buildingId}/updateRoom`, room)
  //       .pipe(map(x => {
  //          const index = this._entity.value.rooms.findIndex(y => y._id === x.data._id);
  //          this._entity.value.rooms[index] = x.data;
  //          return x;
  //       }));
  // }
  //
  // deleteRoom(buildingId: string, roomId: any) {
  //    return this.http.delete<ObjectApiInterface<BuildingRoomModel>>(`${this.baseUrlApi}/${buildingId}/deleteRoom/${roomId}`)
  //       .pipe(map(x => {
  //          const entity = this._rooms.value;
  //          const findIndex = entity.findIndex(y => y._id === x.data?._id);
  //          if (findIndex !== -1) {
  //             entity.splice(findIndex, 1);
  //          }
  //          this._rooms.next(entity);
  //          return x;
  //       }));
  // }

  createActivity(buildingId: string, activity: any) {
    return this.http
      .post<ObjectApiInterface<BuildingActivityModel>>(`${this.baseUrlApi}/${buildingId}/createActivity`, activity)
      .pipe(
        map(x => {
          this._entity.value.activities.push(x.data);
          return x;
        }),
      );
  }
  createRoom(buildingId: string, room: any) {
    return this.http
      .post<ObjectApiInterface<BuildingRoomModel>>(`${this.baseUrlApi}/${buildingId}/createRoom`, room)
      .pipe(
        map(x => {
          const current = this._entity.value;
          const rooms = current?.rooms ?? [];
          this._entity.value.rooms = [...rooms, x.data];
          this._rooms.next(this._entity.value.rooms);
          this.refreshEntity();
          return x;
        }),
      );
  }

  updateActivity(buildingId: string, activity: any) {
    return this.http
      .put<ObjectApiInterface<BuildingActivityModel>>(`${this.baseUrlApi}/${buildingId}/updateActivity`, activity)
      .pipe(
        map(x => {
          const index = this._entity.value.activities.findIndex(y => y._id === x.data._id);
          this._entity.value.activities[index] = x.data;
          return x;
        }),
      );
  }
  updateRoom(buildingId: string, room: any) {
    return this.http
      .put<ObjectApiInterface<BuildingRoomModel>>(`${this.baseUrlApi}/${buildingId}/updateRoom`, room)
      .pipe(
        map(x => {
          const current = this._entity.value;
          const rooms = current?.rooms ?? [];
          const index = rooms.findIndex(y => y._id === x.data._id);
          if (index === -1) {
            this._entity.value.rooms = [...rooms, x.data];
          } else {
            this._entity.value.rooms = [...rooms.slice(0, index), x.data, ...rooms.slice(index + 1)];
            this._rooms.next(this._entity.value.rooms);
          }
          this.refreshEntity();
          return x;
        }),
      );
  }

  deleteActivity(buildingId: string, activityId: any) {
    return this.http
      .delete<
        ObjectApiInterface<BuildingActivityModel>
      >(`${this.baseUrlApi}/${buildingId}/deleteActivity/${activityId}`)
      .pipe(
        map(x => {
          this._entity.value.activities = this._entity.value.activities.filter(y => y._id !== x['data']._id);
          return x;
        }),
      );
  }
  deleteRoom(buildingId: string, roomId: any) {
    return this.http
      .delete<ObjectApiInterface<BuildingRoomModel>>(`${this.baseUrlApi}/${buildingId}/deleteRoom/${roomId}`)
      .pipe(
        map(x => {
          const current = this._entity.value;
          if (current?.rooms) {
            this._entity.value.rooms = current.rooms.filter(y => y._id !== x.data?._id);
            this._rooms.next(this._entity.value.rooms);
          }
          this.refreshEntity();
          return x;
        }),
      );
  }

  addEstablishment(buildingId: string, establishmentId: any, surface: any) {
    return this.http.post<ObjectApiInterface<BuildingModel>>(
      `${this.baseUrlApi}/${buildingId}/addEstablishment/${establishmentId}`,
      { surface },
    );
  }
  updateMainEstablishment(buildingId: string, establishmentId: string, isMain: boolean) {
    return this.http
      .put<
        ObjectApiInterface<Partial<BuildingModel>>
      >(`${this.baseUrlApi}/${buildingId}/updateMainEstablishment/${establishmentId}`, { isMain })
      .pipe(
        map(response => {
          const currentEntity = this._entity.getValue(); // toujours getValue()

          if (currentEntity?.establishments) {
            const updatedEstablishments = currentEntity.establishments.map(establishment => ({
              ...establishment,
              isMain: establishment._id === establishmentId,
              favorite: false,
            }));

            const updatedEntity: BuildingModel = {
              ...currentEntity,
              establishments: [...updatedEstablishments],
            };

            this._entity.next(updatedEntity);
          }
          return response;
        }),
      );
  }

  updateMainAddress(buildingId: string, addressId: any, isMain: boolean) {
    return this.http
      .put<
        ObjectApiInterface<BuildingModel>
      >(`${this.baseUrlApi}/${buildingId}/updateMainAddress/${addressId}`, { isMain })
      .pipe(
        map(response => {
          const currentEntity = this._entity.getValue(); // toujours getValue()
          const updatedEntity: BuildingModel = {
            ...currentEntity,
            establishments: [...currentEntity.establishments],
            addresses: response.data.addresses,
          };
          this._entity.next(updatedEntity);

          // Recharger les adresses depuis AddressService pour avoir isMain calculé par l'aggregation
          this.loadBuildingAddresses(buildingId);

          return response;
        }),
      );
  }

  updateEstablishment(buildingId: string, establishmentId: any, data: any) {
    return this.http
      .put<
        ObjectApiInterface<EstablishmentModel>
      >(`${this.baseUrlApi}/${buildingId}/updateEstablishment/${establishmentId}`, data)
      .pipe(
        map(x => {
          const index = this._entity.value.establishments.findIndex(y => y._id === establishmentId);
          this._entity.value.establishments[index] = x.data;
          this._entity.next(this._entity.value);
          return x;
        }),
      );
  }

  deleteEstablishment(buildingId: string, establishmentId: any) {
    return this.http
      .delete<
        ObjectApiInterface<BuildingModel>
      >(`${this.baseUrlApi}/${buildingId}/deleteEstablishment/${establishmentId}`)
      .pipe(
        map(x => {
          // Le backend retourne le bâtiment complet mis à jour avec les activités enrichies
          x.data.establishments = x.data?.establishments
            ?.filter(est => est._id)
            .map(establishment => {
              establishment.nbOfPermanentBed = establishment.activities?.reduce((p, c) => p + c.nbOfPermanantBed, 0);
              return establishment;
            });
          this._entity.next(x.data);
          return x;
        }),
      );
  }

  downloadExcelRoomTemplate(buildingId: string) {
    return this.http.get<ObjectApiInterface<BuildingRoomModel>>(
      `${this.baseUrlApi}/download-excel-building-room-template/${buildingId}`,
    );
  }

  importRoom(entity, buildingId): Observable<ObjectApiInterface<BuildingRoomModel>> {
    return this.http
      .post<ObjectApiInterface<ActivityModel>>(`${this.baseUrlApi}/import-building-room/${buildingId}`, entity)
      .pipe(
        map((x: any) => {
          this._rooms.next(x.data?.rooms);
          return x;
        }),
      );
  }

  downloadExcelAddressTemplate(): Observable<ObjectApiInterface<AddressModel>> {
    return this.http.get<ObjectApiInterface<AddressModel>>(
      `${this.baseUrlApi}/download-excel-building-address-template`,
    );
  }

  createAddress(buildingId: string, address: AddressModel): Observable<ObjectApiInterface<BuildingModel>> {
    return this.http
      .post<ObjectApiInterface<AddressModel>>(`${this.baseUrlApi}/${buildingId}/createAddress`, address)
      .pipe(
        map((x: any) => {
          this._entity.next(x.data);
          return x;
        }),
      );
  }

  updateAddress(id: string, data: any): Observable<ObjectApiInterface<BuildingModel>> {
    return this.http
      .put<ObjectApiInterface<BuildingModel>>(`${this.baseUrlApi}/${id}/updateAddress/${data._id}`, data)
      .pipe(
        map(x => {
          this._entity.next(x.data);
          return x;
        }),
      );
  }

  deleteAddress(buildingId: string, addressId: string): Observable<ObjectApiInterface<BuildingModel>> {
    return this.http
      .delete<ObjectApiInterface<BuildingModel>>(`${this.baseUrlApi}/${buildingId}/deleteAddress/${addressId}`)
      .pipe(
        map(response => {
          // Mettre à jour l'entité locale avec les données du backend
          this._entity.next(response.data);
          return response;
        }),
      );
  }

  findPerimetersByEstablishments(entity: ContractModel, data: string[]) {
    return this.http
      .post<ObjectApiInterface<BuildingModel[]>>(`${this.baseUrlApi}/find-by-establishments/${entity['_id']}`, { data })
      .pipe(
        map(x => {
          return x.data;
        }),
      );
  }

  transformBuildingData(data: BuildingModel[]) {
    const transformedData = [];

    for (const building of data) {
      if (building.establishments?.length) {
        // Un bâtiment peut avoir plusieurs établissements rattachés. On ne retient que
        // l'établissement favori (isMain), avec fallback sur le premier si aucun favori,
        // afin que le bâtiment soit filtré/affiché via son établissement gestionnaire
        // (cohérent avec getEstablishmentIdsFromBuilding côté backend).
        const establishment = building.establishments.find(e => e.isMain) ?? building.establishments[0];
        const transformedItem = {
          _id: building._id,
          label: building.label,
          genericLabel: building.genericBuilding.label,
          genericId: building.genericBuilding._id,
          establishmentId: establishment._id,
          establishmentLabel: establishment.label,
          buildingId: building._id,
          buildingLabel: building.label,
        };
        transformedData.push({ ...transformedItem });
      }
    }

    transformedData.sort((a, b) => a.label.localeCompare(b.label));
    return transformedData;
  }

  findBuildingsByPrestation(prestationId: string) {
    return this.http
      .post<ObjectApiInterface<BuildingModel[]>>(`${this.baseUrlApi}/find-by-prestation/${prestationId}`, {})
      .pipe(
        map(x => {
          return x.data;
        }),
      );
  }

  setEndServiceDate(id: string, data: any) {
    return this.http.put<ObjectApiInterface<EquipmentModel>>(`${this.baseUrlApi}/${id}/set-end-service`, data).pipe(
      map((x: any) => {
        this._entity.next(x.data);
        return x;
      }),
    );
  }

  loadBuildingAddresses(buildingId: string) {
    console.log('loadBuildingAddresses called for buildingId:', buildingId);
    this.addressService
      .findAll(1, 100, 'label', 'desc', '', {
        buildingIds: [buildingId],
      })
      .subscribe(response => {
        console.log('loadBuildingAddresses response:', response);
        this._address.next(response.data);
      });
  }

  /**
   * Crée une metrik (statut temporaire actif/inactif) pour un bâtiment
   * Endpoint: PUT /createMetrik/:_id
   */
  createMetrik(buildingId: string, metrik: BuildingMetrikStatusModel): Observable<ObjectApiInterface<BuildingModel>> {
    return this.http
      .put<ObjectApiInterface<BuildingModel>>(`${this.baseUrlApi}/createMetrik/${buildingId}`, metrik)
      .pipe(
        map(response => {
          this._entity.next(response.data);
          return response;
        }),
      );
  }

  /**
   * Récupère les données de préparation pour la checklist de fin de service
   */
  getEndServicePreparationData(buildingId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrlApi}/${buildingId}/end-service-preparation`);
  }

  /**
   * Met à jour la checklist de préparation de fin de service
   */
  updateEndServiceChecklist(buildingId: string, checklist: any): Observable<ObjectApiInterface<BuildingModel>> {
    return this.http
      .put<ObjectApiInterface<BuildingModel>>(`${this.baseUrlApi}/${buildingId}/end-service-checklist`, checklist)
      .pipe(
        map(response => {
          // NE PAS remplacer toute l'entité, juste mettre à jour le champ checklist
          // pour éviter de perdre establishments, energies, etc.
          const currentEntity = this._entity.getValue();
          if (currentEntity && response.data?.endServicePreparationChecklist) {
            currentEntity.endServicePreparationChecklist = response.data.endServicePreparationChecklist;
            this._entity.next(currentEntity);
          }
          return response;
        }),
      );
  }

  /**
   * Valide que toutes les actions préalables sont complètes
   */
  validateEndServicePreparation(buildingId: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrlApi}/${buildingId}/validate-end-service`, {});
  }

  /**
   * Re-vérifie les dépendances de fin de service
   */
  recheckEndServiceDependencies(buildingId: string): Observable<ObjectApiInterface<any>> {
    return this.http.post<ObjectApiInterface<any>>(
      `${this.baseUrlApi}/${buildingId}/recheck-end-service-dependencies`,
      {},
    );
  }

  findMainCities(
    page: number,
    perPage: number | 'all',
    search: string = '',
  ): Observable<ObjectApiInterface<BuildingModel[]>> {
    return this.http.get<ObjectApiInterface<BuildingModel[]>>(`${this.baseUrlApi}/main-cities`, {
      params: new HttpParams()
        .set('page', page.toString())
        .set('perPage', perPage.toString())
        .set('sort', 'mainCity@asc')
        .set('search', search)
        .set('filters', '{}'),
    });
  }
}
